import { GetCMSContent } from "aldoo-ra/CMS/api"

/**
 * Feature Matrix bullets grouped by plan pid, in feature sort order.
 * A row is skipped for a plan when there's no PlanFeatureValue entry, or
 * its param is "✗"/"—" (the Feature Matrix editor's not-applicable markers).
 *
 * Matches aldoo-transfer-web-app's Site/Pricing/helpers.jsx
 * (getHighlightEntry): the CMS "param" field holds only the value, e.g.
 * "3 days" — the bullet's leading text is the feature's own name, e.g.
 * "Max link duration". A "✓" param means a boolean feature with no value
 * to show (bullet is just the feature name).
 * Returns { [pid]: [{ id, name, value }] }
 */
export async function fetchPlanFeatures() {
  const [plansRes, featuresRes, pfvRes] = await Promise.all([
    GetCMSContent({ match: { "content.type": "ProductPlan" }, itemsPerPage: 50, expand: false }),
    GetCMSContent({ match: { "content.type": "ProductFeature" }, itemsPerPage: 200, expand: false }),
    GetCMSContent({ match: { "content.type": "PlanFeatureValue" }, itemsPerPage: 1000, expand: false }),
  ])

  const planPidById = new Map()
  for (const d of plansRes?.data || []) {
    planPidById.set(String(d._id), d.content?.id)
  }

  const pfvById = new Map()
  for (const d of pfvRes?.data || []) {
    pfvById.set(String(d._id), d.content || {})
  }

  const features = (featuresRes?.data || [])
    .map((d) => d.content || {})
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  const byPid = {}
  for (const feature of features) {
    const valueIds = (feature.plan_values || []).map((ref) =>
      String(typeof ref === "string" ? ref : ref?._id || ref?.item)
    )
    for (const valueId of valueIds) {
      const pfv = pfvById.get(valueId)
      if (!pfv) continue
      let plan = pfv.plan
      if (plan && typeof plan === "object") plan = plan._id || plan.item
      const pid = planPidById.get(String(plan))
      if (!pid) continue
      const param = pfv.param
      if (!param || param === "✗" || param === "—") continue
      const value = param === "✓" ? null : param
      if (!byPid[pid]) byPid[pid] = []
      byPid[pid].push({ id: feature.id, name: feature.name, value })
    }
  }
  return byPid
}